import React, { useState, useEffect } from "react";

/**
 * NetworkStatus component displays the current network connectivity status
 * and provides user feedback when the connection is lost or restored
 */
const NetworkStatus = ({
  showWhenOnline = false,
  position = "bottom-right",
  className = "",
}) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [wasOffline, setWasOffline] = useState(false);
  const [showReconnected, setShowReconnected] = useState(false);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      if (wasOffline) {
        setShowReconnected(true);
        setTimeout(() => setShowReconnected(false), 3000);
      }
      setWasOffline(false);
    };

    const handleOffline = () => {
      setIsOnline(false);
      setWasOffline(true);
      setShowReconnected(false);
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [wasOffline]);

  // Don't show anything if online and showWhenOnline is false
  if (isOnline && !showWhenOnline && !showReconnected) {
    return null;
  }

  const statusClasses = [
    "network-status",
    `network-status-${position}`,
    isOnline ? "network-status-online" : "network-status-offline",
    showReconnected ? "network-status-reconnected" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const getStatusMessage = () => {
    if (showReconnected) {
      return "Connection restored";
    }
    return isOnline ? "Online" : "No internet connection";
  };

  const getStatusIcon = () => {
    if (showReconnected) {
      return "✅";
    }
    return isOnline ? "🟢" : "🔴";
  };

  return (
    <div className={statusClasses}>
      <div className="network-status-content">
        <span className="network-status-icon">{getStatusIcon()}</span>
        <span className="network-status-text">{getStatusMessage()}</span>
      </div>
    </div>
  );
};

/**
 * Hook for monitoring network status
 */
export const useNetworkStatus = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [connectionType, setConnectionType] = useState(null);
  const [effectiveType, setEffectiveType] = useState(null);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    // Get connection information if available
    if ("connection" in navigator) {
      const connection =
        navigator.connection ||
        navigator.mozConnection ||
        navigator.webkitConnection;
      if (connection) {
        setConnectionType(connection.type);
        setEffectiveType(connection.effectiveType);

        const handleConnectionChange = () => {
          setConnectionType(connection.type);
          setEffectiveType(connection.effectiveType);
        };

        connection.addEventListener("change", handleConnectionChange);

        return () => {
          window.removeEventListener("online", handleOnline);
          window.removeEventListener("offline", handleOffline);
          connection.removeEventListener("change", handleConnectionChange);
        };
      }
    }

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  return {
    isOnline,
    connectionType,
    effectiveType,
    isSlowConnection: effectiveType === "slow-2g" || effectiveType === "2g",
  };
};

export default NetworkStatus;
